var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import Loader from 'pages/Test/localComponent/loader';
function ReactHlsPlayer(props) {
    const [isLoading, setIsLoading] = useState(true);
    var hlsConfig = props.hlsConfig;
    var _b = props.playerRef;
    var playerRef = _b === void 0 ? React.createRef() : _b;
    var src = props.src;
    var autoPlay = props.autoPlay;
    var liveCheckCallback = props.liveCheckCallback?props.liveCheckCallback:()=>{};
    var onEndCallback = props.onEndCallback ? props.onEndCallback : () => {};
    var videoProps = __rest(props, ["hlsConfig", "playerRef", "src", "autoPlay", "liveCheckCallback", "onEndCallback", "onPersistentStall"]);
    /*
     * "This viewer cannot hold the rendition they are on."
     *
     * Asked before the rebuild below, and the reason it exists: the backend publishes one
     * folder per rendition and no master playlist, so every playlist this player loads has
     * exactly one level in it. hls.js therefore has no ABR to do — there is nothing to
     * switch down to — and a viewer who picks 720p is pinned to 720p for the rest of the
     * class however their line behaves. Rebuilding at the same URL, which is all this could
     * do on its own, restarts the stall it was called for.
     *
     * The owner of the quality selector is the only thing that can actually answer a viewer
     * running out of bandwidth, because for this backend "switch level" means "load a
     * different URL". Returning true means it has done so and a new src is on the way, so
     * the rebuild is skipped — the src change re-runs the effect and builds the player once
     * instead of twice.
     *
     * Held in a ref, not a dependency. The effect below tears down and rebuilds hls.js when
     * its dependencies change, and a handler passed inline from a parent is a new function
     * on every render — listing it would rebuild the player several times a second.
     */
    const onPersistentStallRef = useRef(props.onPersistentStall);
    onPersistentStallRef.current = props.onPersistentStall;
    // "Is this viewer at the live edge?" — the question behind the red Live dot and the
    // Go to live button.
    //
    // The threshold was a fixed 8s, below the latency the pipeline is built for: hls.js
    // deliberately holds playback liveSyncDurationCount x TARGETDURATION behind the edge,
    // which is 12s against this backend's 4s segments. A viewer sitting exactly where hls.js
    // put them was therefore reported as NOT live, permanently — the dot stayed grey for
    // everyone and Go to live always had something to do, whether or not anything was wrong.
    //
    // hls.maxLatency is the honest boundary: it is the distance past which hls.js itself
    // seeks forward to catch up, so inside it the viewer is as live as this stream gets.
    // It reads 0 before the first playlist is parsed, and Infinity wherever the config has
    // not named a ceiling (this component is used by other players too) — neither is a
    // usable threshold, so the old 8s stands in for both.
    // Per instance, not per module. This was a bare `let` at file scope, so two players on a
    // page shared one handle and whichever started second orphaned the first one's interval —
    // and, worse, recoverMediaError re-attaches the media, which used to re-run everything
    // registered inside MEDIA_ATTACHED below. A class that hit a few media errors accumulated
    // a live check per error, all of them writing to the same callback, none of them
    // cancellable. A ref makes the handle belong to the player that owns it, and
    // startLiveCheck clears its own before starting a new one.
    const liveCheckRef = useRef(null);
    const stopLiveCheck = () => {
        if (liveCheckRef.current) {
            clearInterval(liveCheckRef.current);
            liveCheckRef.current = null;
        }
    };
    const startLiveCheck = (hlsInstance) => {
        stopLiveCheck();
        liveCheckRef.current = setInterval(() => {
            if (!playerRef.current) return
            const behindLive = playerRef.current.duration - playerRef.current.currentTime;
            const ceiling = hlsInstance?.maxLatency;
            const threshold = (Number.isFinite(ceiling) && ceiling > 0) ? ceiling : 8;
            liveCheckCallback(behindLive < threshold);
        }, 1000); // Check every second
    };
    useEffect(function () {
        var hls;
        // Cancels a pending manifest retry. Reassigned by each _initPlayer so unmounting, or
        // a re-init, cannot leave a timer that fires against a destroyed instance.
        var manifestRetryCancel = function () {};
        // Stall bookkeeping for the auto-rebuild below. Declared out here rather than inside
        // _initPlayer on purpose: a rebuild must not wipe its own cooldown, or a player that
        // keeps stalling would rebuild itself in a loop and never settle.
        var STALL_WINDOW_MS = 30000;      // how far back "recurring" looks
        var STALL_LIMIT = 3;              // stalls inside that window before rebuilding
        var REBUILD_COOLDOWN_MS = 60000;  // never more than one rebuild a minute
        var stallTimes = [];
        var lastRebuiltAt = 0;
        function _initPlayer() {
            manifestRetryCancel();
            setIsLoading(true)
            if (hls != null) {
                hls.destroy();
            }
            var newHls = new Hls(__assign({ enableWorker: true }, hlsConfig));
            if (playerRef.current != null) {
                newHls.attachMedia(playerRef.current);
            }
            /*
             * HLS resolution verification.
             *
             * The backend publishes one rendition per folder — /<room>/360/index.m3u8 and so
             * on — so the URL states an intent, not a fact. Nothing about requesting the 720
             * playlist proves 720 lines came back: the ladder can be misconfigured, all three
             * folders can be fed from the same encoder output, or a rendition can be stale.
             *
             * The decisive number is the video element's own videoWidth/videoHeight, which is
             * what the decoder produced. Everything else logged here explains it: the manifest
             * says what the playlist claims, the fragment URL says which folder the bytes
             * actually came from.
             */
            const hlsLog = (...args) => console.log(
                '%c[HLS]%c ' + args[0],
                'color:#fff;background:#0369a1;padding:1px 5px;border-radius:3px;font-weight:700', '',
                ...args.slice(1));

            hlsLog('loadSource: ' + src);

            // MEDIA_ATTACHED does one thing: start loading. It used to also register
            // MANIFEST_PARSED from inside itself, which reads as "set this up once" and is
            // not — recoverMediaError() below detaches and re-attaches the media, so every
            // media error added another MANIFEST_PARSED handler to the same instance. Each
            // one then fired on the next manifest, so one recovered error meant two play()
            // calls and two live checks, two errors meant three, and a long class on a poor
            // line stacked them until the log was unreadable and the intervals were fighting
            // over the Live dot. Registered once, at the same level, it happens once.
            newHls.on(Hls.Events.MEDIA_ATTACHED, function () {
                newHls.loadSource(src);
            });
            newHls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
                var _a;
                // What the playlist declares. A single-rendition playlist reports one
                // level; if RESOLUTION is absent the server did not put it in the manifest,
                // which is worth knowing on its own.
                hlsLog('manifest parsed — levels the playlist declares:',
                    (data?.levels ?? newHls.levels ?? []).map((level, index) => ({
                        index,
                        declared: level.width && level.height ? level.width + '×' + level.height : '(no RESOLUTION tag)',
                        bitrate_kbps: level.bitrate ? Math.round(level.bitrate / 1000) : null,
                        codecs: level.codecSet ?? level.videoCodec ?? null,
                    })));
                if (autoPlay) {
                    (_a = playerRef === null || playerRef === void 0 ? void 0 : playerRef.current) === null || _a === void 0 ? void 0 : _a.play().catch(function () {
                        return console.log('Unable to autoplay prior to user interaction with the dom.');
                    });
                }
                startLiveCheck(newHls);
            });

            // The ground truth. Fires once the decoder knows the real frame size, and again
            // whenever it changes — so switching quality should produce a new line here, and
            // if it does not, the switch did nothing.
            const logRealSize = (why) => {
                const element = playerRef.current;
                if (!element || !element.videoWidth) return
                hlsLog(`DECODED SIZE (${why}): ${element.videoWidth}×${element.videoHeight}  — src: ${src}`);
            };
            if (playerRef.current){
                playerRef.current.addEventListener('loadedmetadata', () => logRealSize('loadedmetadata'));
                playerRef.current.addEventListener('resize', () => logRealSize('resize'));
            }

            newHls.on(Hls.Events.LEVEL_SWITCHED, function (event, data) {
                const level = newHls.levels?.[data?.level];
                hlsLog(`level switched -> ${data?.level}`,
                    level ? {declared: (level.width ?? '?') + '×' + (level.height ?? '?'), url: level.url?.[0]} : null);
                logRealSize('level switch');
            });

            // Which folder the bytes are actually coming from. Logged only when the path
            // changes, so a live stream does not print a line per segment.
            let lastFragDir = null;
            newHls.on(Hls.Events.FRAG_CHANGED, function (event, data) {
                const url = data?.frag?.url ?? '';
                const dir = url.slice(0, url.lastIndexOf('/'));
                if (dir === lastFragDir) return
                lastFragDir = dir;
                hlsLog('now fetching segments from: ' + dir);
                logRealSize('first fragment from new path');
            });
            // #EXT-X-ENDLIST: the encoder has finished and the playlist is complete. hls.js
            // reports that as details.live flipping to false, and from here the stream is a
            // VOD — the latency controller stands down and playback simply runs to the end.
            // Worth saying out loud because the broadcast page's end-of-class drain is timed
            // against exactly this: everything between here and the end is tail the viewer
            // has not seen yet, and used to be discarded when the player was torn down.
            //
            // The same handler answers the backend's "never replay a live stream". An ENDLIST
            // manifest is a VOD playlist, and a player reaching the end of a VOD and starting
            // it again is correct VOD behaviour — which is exactly how a student ended up
            // watching the previous class on a loop. The distinction that matters is *when*
            // the ENDLIST turned up:
            //
            //   - it appears while we are playing  -> the class just ended. Play the tail out.
            //   - it is already there on the first manifest -> we have joined a stream that is
            //     already over, and those four segments are the last class. Never play them.
            //
            // sawLiveManifest is what tells the two apart, and the second case reports ended
            // immediately instead of showing stale content.
            let endlistSeen = false;
            let sawLiveManifest = false;
            let lastStartSN = null;
            let lastGeneration = null;
            // A new encoder means a new stream, even on the same URL. The backend names two
            // tells: #EXT-X-MEDIA-SEQUENCE going backwards, and the g<id> prefix on the
            // segment names changing. Continuing across either of those plays one encoder's
            // segments against another's timeline, so this re-initialises instead.
            const generationOf = (details) => {
                const name = details?.fragments?.[0]?.relurl
                    ?? (details?.fragments?.[0]?.url ?? '').split('/').pop();
                const match = /^(g[^_]*)_/.exec(name || '');
                return match ? match[1] : null;
            };
            newHls.on(Hls.Events.LEVEL_LOADED, function (event, data) {
                const details = data?.details;
                if (!details) return

                const startSN = details.startSN;
                const generation = generationOf(details);
                const wentBackwards = lastStartSN !== null && Number.isFinite(startSN) && startSN < lastStartSN;
                const changedGeneration = lastGeneration !== null && generation !== null && generation !== lastGeneration;
                if (wentBackwards || changedGeneration){
                    hlsLog('generation change — '
                        + (wentBackwards ? `MEDIA-SEQUENCE went ${lastStartSN} -> ${startSN}` : `segment prefix ${lastGeneration} -> ${generation}`)
                        + '; re-initialising rather than continuing');
                    _initPlayer();
                    return
                }
                if (Number.isFinite(startSN)) lastStartSN = startSN;
                if (generation !== null) lastGeneration = generation;

                if (details.live){ sawLiveManifest = true; return }
                if (endlistSeen) return
                endlistSeen = true;

                if (!sawLiveManifest){
                    // Joined onto a finished playlist. This is the looping-old-class case.
                    hlsLog('ENDLIST on the first manifest — this stream has already ended; '
                        + 'not playing the tail of a previous class');
                    newHls.stopLoad();
                    onEndCallback();
                    return
                }
                const element = playerRef.current;
                const remaining = element && Number.isFinite(element.duration)
                    ? (element.duration - element.currentTime).toFixed(1) + 's'
                    : 'unknown';
                hlsLog('ENDLIST seen — stream complete, ' + remaining + ' still to play out');
            });
            newHls.on(Hls.Events.BUFFERING, () => {
                setIsLoading(true);
            });

            newHls.on(Hls.Events.BUFFER_APPENDED, () => {
                setIsLoading(false);
            });

            // A 404 on a live manifest is normal, not fatal.
            //
            // On a camera-to-whiteboard switch the outgoing encoder ends seconds before its
            // replacement starts, so for that gap the playlist genuinely is not there. Giving
            // up on the first 404 turns an ordinary switch into a dead player — and it is also
            // what blocks the backend from enabling its stale-manifest guard, which answers
            // 404 deliberately to stop a finished class being served forever.
            //
            // So a 404 opens a window rather than ending anything: keep asking for ~10s, and
            // only once nothing has come back in that time treat the stream as gone. The
            // window is reset by any successful manifest, so a second switch later in the
            // class gets its own full ten seconds.
            const MANIFEST_404_WINDOW_MS = 10000;
            let first404At = 0;
            let retryTimer = null;
            manifestRetryCancel = () => {
                if (retryTimer) clearTimeout(retryTimer);
                retryTimer = null;
            };
            newHls.on(Hls.Events.LEVEL_LOADED, function () { first404At = 0 });
            newHls.on(Hls.Events.ERROR, function (event, data) {
                console.log("eee error", event, data)
                setIsLoading(true);

                const isManifest = data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR
                    || data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR
                    || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT;
                const is404 = data.response?.code === 404;
                if (isManifest && is404){
                    const now = Date.now();
                    if (!first404At) first404At = now;
                    if (now - first404At < MANIFEST_404_WINDOW_MS){
                        hlsLog(`manifest 404 — retrying (${Math.round((now - first404At) / 1000)}s of `
                            + `${MANIFEST_404_WINDOW_MS / 1000}s); an encoder switch looks like this`);
                        if (retryTimer) return
                        retryTimer = setTimeout(() => {
                            retryTimer = null;
                            try { newHls.loadSource(src); newHls.startLoad(); } catch (e) { /* torn down */ }
                        }, 1000);
                        return
                    }
                    hlsLog('manifest 404 for ' + (MANIFEST_404_WINDOW_MS / 1000) + 's — treating the stream as ended');
                    newHls.stopLoad();
                    onEndCallback();
                    return
                }

                // Stalls that keep coming back, handled the way a person handles them.
                //
                // The reliable workaround for the audio degrading mid-class has been to change
                // quality on the player menu — which does not fix anything about the quality,
                // it just changes the URL, which rebuilds hls.js and hands playback a fresh
                // MediaSource. The fault accumulates in the buffer and a restart clears it.
                //
                // So do that automatically instead of waiting for someone to notice. These are
                // non-fatal errors, so hls.js recovers each one on its own and the switch below
                // never sees them; what it cannot see is that they are *recurring*, which is
                // the actual signal. Three inside thirty seconds is a player in trouble rather
                // than a line with a hiccup.
                //
                // Throttled hard, because a rebuild is not free — it costs the viewer a second
                // or two of video, and a recovery loop would be worse than the stutter it is
                // trying to fix.
                const isStall = data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR
                    || data.details === Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL
                    || data.details === Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE;
                if (isStall && !data.fatal){
                    const now = Date.now();
                    stallTimes.push(now);
                    while (stallTimes.length && now - stallTimes[0] > STALL_WINDOW_MS) stallTimes.shift();
                    if (stallTimes.length >= STALL_LIMIT && now - lastRebuiltAt > REBUILD_COOLDOWN_MS){
                        lastRebuiltAt = now;
                        stallTimes = [];
                        // Ask for a smaller rendition first. A rebuild clears a buffer that
                        // has gone wrong; it does nothing for a viewer whose downlink cannot
                        // carry what they asked for, and at 720p that is much the likelier
                        // of the two — the rebuild would land on the same URL and stall its
                        // way back here a minute later, each attempt costing a second or two
                        // of class. Stepping down is the thing hls.js would have done by
                        // itself if these renditions shared one playlist.
                        let stepped = false;
                        try { stepped = onPersistentStallRef.current?.() === true }
                        catch (error) { stepped = false }
                        if (stepped){
                            hlsLog(`${STALL_LIMIT} buffer stalls inside ${STALL_WINDOW_MS / 1000}s `
                                + '— stepping down a rendition; the new src rebuilds the player');
                            return
                        }
                        hlsLog(`${STALL_LIMIT} buffer stalls inside ${STALL_WINDOW_MS / 1000}s `
                            + '— rebuilding the player, which is what changing quality by hand was doing');
                        _initPlayer();
                        return
                    }
                }

                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            newHls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            newHls.recoverMediaError();
                            break;
                        default:
                            _initPlayer();
                            break;
                    }
                }
            });
            hls = newHls;
        }
        if (Hls.isSupported()) {
            _initPlayer();
        }
        return function () {
            stopLiveCheck();
            manifestRetryCancel();
            if (hls != null) {
                hls.destroy();
            }
        };
    }, [autoPlay, hlsConfig, playerRef, src]);
    if (Hls.isSupported())
        return (
            <div style={{position:"relative"}}>
            <video ref={playerRef} onEnded={onEndCallback} {...videoProps}/>
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 0,
                    color: '#fff',
                    padding: '20px 25px',
                    borderRadius: '5px',
                    background:"rgba(0, 0, 0, 0.4)",
                    pointerEvents:"none"
                }} onClick={(e)=>{e.stopPropagation()}}>
                    <Loader style={{color:"lightgray", fontSize:40}} />
                    <div className='m-t-15 disable-text-selection' style={{color:"lightgray", fontWeight:'bold'}}>Buffering .....</div>
                </div>
            )}
            </div>
        )
    return React.createElement("video", __assign({ ref: playerRef, src: src, autoPlay: autoPlay }, videoProps));
}
export default ReactHlsPlayer;
